import { getTopLevelElementId } from '@/modules/map-editor';
import type { HoverDetectionResult } from '@/modules/map-editor';
import type { Node, Edge } from '@/modules/map-editor';

/**
 * Service for detecting which elements are under the pointer.
 * Handles hover detection for nodes and edges by running hit-tests
 * and determining the top-level hovered element.
 */
export class HoverDetectionService {
  private nodes: Node[];
  private edges: Edge[];

  constructor(nodes: Node[], edges: Edge[]) {
    this.nodes = nodes;
    this.edges = edges;
  }

  /**
   * Update element references without recreating the service.
   *
   * @param nodes - Current node array
   * @param edges - Current edge array
   */
  updateElements(nodes: Node[], edges: Edge[]): void {
    this.nodes = nodes;
    this.edges = edges;
  }

  /**
   * Detect which node or edge is under the pointer.
   * Runs checkHovered() on each element (which sets the element's `hover` flag
   * as a side effect), then picks the top-level hovered ID via z-order.
   *
   * @param ctx - Canvas 2D context for hit-testing
   * @param position - Current pointer position in canvas coordinates (only x/y are used)
   * @param zoomLevel - Current zoom level for edge hit-testing
   * @returns IDs of the top-level hovered node and edge (-1 if none)
   */
  detectHover(
    ctx: CanvasRenderingContext2D,
    position: { x: number; y: number },
    zoomLevel: number
  ): HoverDetectionResult {
    // Run hit-tests on all unlocked nodes (sets node.hover as side effect)
    for (const node of this.nodes) {
      if (!node.locked) {
        node.checkHovered(ctx, position);
      }
    }

    const hoveredNodeId = getTopLevelElementId(this.nodes);

    // Run hit-tests on edges only when no node is hovered
    if (hoveredNodeId === -1) {
      for (const edge of this.edges) {
        edge.checkHovered(ctx, position, zoomLevel);
      }
    }

    const hoveredEdgeId = getTopLevelElementId(this.edges);

    return {
      hoveredNodeId,
      hoveredEdgeId
    };
  }

  /**
   * Clear hover flags from elements that are no longer under the pointer.
   *
   * @param result - Latest detection result
   */
  updateHoverState(result: HoverDetectionResult): void {
    // Clear hover from nodes that are no longer hovered
    for (const node of this.nodes) {
      if (node.hover && node.id !== result.hoveredNodeId) {
        node.hover = false;
      }
    }

    // Clear hover from edges that are no longer hovered
    for (const edge of this.edges) {
      if (edge.hover && edge.id !== result.hoveredEdgeId) {
        edge.hover = false;
      }
    }
  }
}